import { Link } from 'react-router-dom';

import { useCollections } from '@/features/collections/useCollections';
import type { PhotoshootDetail, PhotoshootShotEntry } from '@/types/photoshoot';
import { describeSelections } from '@/utils/selections';

import { usePhotoshootResultQuery } from './usePhotoshootResultQuery';

interface PhotoshootResultScreenProps {
  photoshootId: string;
}

type PlaceholderState = 'failed' | 'processing' | 'queued';

type Tile =
  | { position: number; state: 'completed'; generationId: string; thumbnailUrl: string | null }
  | { position: number; state: PlaceholderState };

const PLACEHOLDER_LABELS: Record<PlaceholderState, string> = {
  queued: 'В очереди',
  processing: 'Создаётся…',
  failed: 'Кадр не получился',
};

/**
 * Строит ровно `size` тайлов, а не только те, для которых уже есть запись.
 *
 * Серия завершена — воркер больше ни к чему не вернётся, поэтому всё, что
 * не стало готовым кадром, для человека «не получилось»: и кадр без записи
 * (упал до создания generations — кончились кредиты, сработал предохранитель),
 * и кадр, застрявший в обработке при обрыве воркера. Иначе такой тайл вечно
 * выглядел бы «в очереди» или «создаётся».
 *
 * Серия ещё идёт — кадр либо уже создаётся (запись есть, статус не финальный),
 * либо ждёт своей очереди у планового воркера (записи ещё нет). Сорвавшимся
 * считается только кадр, который сам сообщил об ошибке.
 */
function buildTiles(data: PhotoshootDetail): Tile[] {
  const byPosition = new Map<number, PhotoshootShotEntry>(
    data.shots.map((shot) => [shot.position, shot]),
  );
  const finished = data.status === 'completed';

  return Array.from({ length: data.size }, (_, position): Tile => {
    const shot = byPosition.get(position);

    if (shot?.status === 'completed') {
      return {
        position,
        state: 'completed',
        generationId: shot.generationId,
        thumbnailUrl: shot.thumbnailUrl,
      };
    }

    if (finished || shot?.status === 'failed') {
      return { position, state: 'failed' };
    }

    return { position, state: shot ? 'processing' : 'queued' };
  });
}

/**
 * Результат фотосессии — плитка кадров.
 *
 * Кадр открывается обычным экраном результата: скачивание, избранное и
 * шаринг живут там и здесь не дублируются. Сорвавшийся кадр остаётся в
 * серии видимым — серия из 10 кадров, где молча показали 8, выглядела бы
 * как потерянные кредиты. Кадры доделывает плановый воркер, поэтому пока
 * серия не завершена, экран сам обновляется (usePhotoshootResultQuery).
 */
export function PhotoshootResultScreen({ photoshootId }: PhotoshootResultScreenProps) {
  const { data, isPending, isError, refetch } = usePhotoshootResultQuery(photoshootId);
  const { byId } = useCollections();

  if (isPending) {
    return <p className="px-6 py-8 text-text-2">Загрузка фотосессии…</p>;
  }

  if (isError) {
    return (
      <div className="px-6 py-8">
        <p className="text-danger">Не удалось загрузить фотосессию. Попробуйте ещё раз.</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-2 text-sm text-accent hover:underline"
        >
          Повторить
        </button>
      </div>
    );
  }

  if (!data) {
    return <p className="px-6 py-8 text-text-2">Фотосессия не найдена.</p>;
  }

  const collection = byId.get(data.collectionId);
  const collectionTitle = collection?.title ?? data.collectionId;
  const tiles = buildTiles(data);
  const ready = tiles.filter((tile) => tile.state === 'completed').length;
  const inProgress = data.status !== 'completed';

  return (
    <div className="flex flex-col gap-5 px-6 py-8">
      <div>
        <p className="text-sm text-text-3">{collectionTitle}</p>
        <p className="text-xs text-text-3">{describeSelections(collection, data.selections)}</p>
        <p className="mt-2 text-sm text-text-2">
          Готово {ready} из {data.size} кадров
          {inProgress && ' — создаём остальные…'}
        </p>
      </div>

      {/* Тот же принцип, что в ResultScreen: на mock-провайдере результат не настоящий. */}
      {data.provider === 'mock' && (
        <p className="rounded-card border border-border bg-surface px-3 py-2 text-xs text-text-2">
          Демонстрационный режим: реальная генерация не подключена, изображения
          сделаны заглушкой.
        </p>
      )}

      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {tiles.map((tile) => (
          <li key={tile.position}>
            {tile.state === 'completed' ? (
              <Link
                to={`/result/${tile.generationId}`}
                aria-label={`Кадр ${tile.position + 1}`}
                className="block overflow-hidden rounded-card border border-border bg-surface hover:border-border-strong"
              >
                {tile.thumbnailUrl ? (
                  <img
                    src={tile.thumbnailUrl}
                    alt=""
                    className="aspect-3/4 w-full object-cover"
                  />
                ) : (
                  <div className="flex aspect-3/4 w-full items-center justify-center text-xs text-text-3">
                    Превью недоступно
                  </div>
                )}
              </Link>
            ) : (
              <div className="flex aspect-3/4 w-full items-center justify-center rounded-card border border-border bg-surface p-2 text-center text-xs text-text-3">
                {PLACEHOLDER_LABELS[tile.state]}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
